import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Gauge, IndianRupee, CheckCircle2, XCircle, MinusCircle, ShoppingBag, MessageCircle, StickyNote, Trash2, Plus } from "lucide-react";
import api from "@/lib/api";

const STATUS_ICON = { sent: CheckCircle2, failed: XCircle, skipped: MinusCircle, skipped_not_configured: MinusCircle };

export default function ContactDetailDrawer({ contactId, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("profile");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = () => {
    api.get(`/admin/crm/contacts/${contactId}/detail`).then((r) => setDetail(r.data)).catch(() => toast.error("Could not load contact"));
  };

  useEffect(() => { load(); }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/crm/contacts/${contactId}/notes`, { text: noteText });
      setNoteText("");
      load();
    } catch {
      toast.error("Could not add note");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    await api.delete(`/admin/crm/contacts/${contactId}/notes/${noteId}`);
    load();
  };

  if (!detail) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" data-testid="crm-contact-drawer-loading">
        <div className="w-full sm:w-[480px] h-full bg-[#0b0f1a] p-6"><p className="text-slate-400 text-sm">Loading contact…</p></div>
      </div>
    );
  }

  const { contact, lifetime_value, engagement_score, purchases, messages } = detail;
  const timeline = [
    ...purchases.map((p) => ({ type: "purchase", date: p.completed_at || p.created_at, data: p })),
    ...messages.map((m) => ({ type: "message", date: m.created_at, data: m })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={onClose} data-testid="crm-contact-drawer">
      <div className="w-full sm:w-[480px] h-full bg-[#0b0f1a] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-start justify-between sticky top-0 bg-[#0b0f1a] z-10">
          <div>
            <p className="text-base font-semibold text-white" data-testid="crm-drawer-contact-name">{contact.name}</p>
            <p className="text-xs text-slate-400">{contact.email}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="flex items-center gap-1 text-xs text-amber-300"><Gauge className="w-3.5 h-3.5" /> {engagement_score}/100</span>
              <span className="flex items-center gap-1 text-xs text-emerald-300"><IndianRupee className="w-3.5 h-3.5" /> {lifetime_value} LTV</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="crm-drawer-close-btn"><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
        </div>

        <div className="flex border-b border-white/10 px-5">
          {["profile", "activity", "notes"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`crm-drawer-tab-${t}`}
              className={`text-xs font-semibold px-3 py-3 capitalize border-b-2 -mb-px ${tab === t ? "border-primary text-white" : "border-transparent text-slate-500"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "profile" && (
            <div className="space-y-3 text-sm" data-testid="crm-drawer-profile-tab">
              {[
                ["Phone", contact.phone], ["WhatsApp", contact.whatsapp_number], ["Company", contact.company],
                ["Job Title", contact.job_title], ["Country", contact.country], ["State", contact.state], ["City", contact.city],
                ["Interested Course", contact.interested_course], ["Lead Source", contact.lead_source],
                ["Role", contact.role], ["Status", contact.status], ["Last Login", contact.last_login ? new Date(contact.last_login).toLocaleString() : "Never"],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-slate-500">{label}</span>
                  <span className="text-slate-200">{val || "—"}</span>
                </div>
              ))}
              <div className="flex gap-1.5 flex-wrap pt-1">
                {(contact.tags || []).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{t}</span>)}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 text-xs text-slate-400">
                <span>Email consent: {contact.email_consent ? "Yes" : "No"}</span>
                <span>SMS consent: {contact.sms_consent ? "Yes" : "No"}</span>
                <span>WhatsApp consent: {contact.whatsapp_consent ? "Yes" : "No"}</span>
                <span>Marketing consent: {contact.marketing_consent ? "Yes" : "No"}</span>
              </div>
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-3" data-testid="crm-drawer-activity-tab">
              {timeline.length === 0 && <p className="text-xs text-slate-500">No activity yet.</p>}
              {timeline.map((item, i) => {
                if (item.type === "purchase") {
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs" data-testid={`crm-timeline-purchase-${i}`}>
                      <ShoppingBag className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      <div><p className="text-slate-200">Purchased {item.data.package_id}{item.data.amount ? ` — ₹${item.data.amount}` : ""}</p>
                        <p className="text-slate-500">{new Date(item.date).toLocaleString()}</p></div>
                    </div>
                  );
                }
                const Icon = STATUS_ICON[item.data.status] || MessageCircle;
                return (
                  <div key={i} className="flex items-start gap-2 text-xs" data-testid={`crm-timeline-message-${i}`}>
                    <Icon className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
                    <div><p className="text-slate-200">{item.data.channel} via "{item.data.campaign_name}" — {item.data.status}</p>
                      <p className="text-slate-500">{new Date(item.date).toLocaleString()}</p></div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "notes" && (
            <div data-testid="crm-drawer-notes-tab">
              <div className="flex gap-2 mb-4">
                <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" data-testid="crm-note-input"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
                <button onClick={addNote} disabled={savingNote} data-testid="crm-add-note-btn" className="bg-primary hover:bg-primary/90 text-white rounded-lg px-3"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="space-y-2">
                {(contact.notes || []).slice().reverse().map((n) => (
                  <div key={n.id} className="flex items-start justify-between gap-2 bg-white/5 rounded-lg p-3" data-testid={`crm-note-${n.id}`}>
                    <div className="flex items-start gap-2">
                      <StickyNote className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
                      <div><p className="text-xs text-slate-200">{n.text}</p><p className="text-[10px] text-slate-500 mt-0.5">{n.created_by} · {new Date(n.created_at).toLocaleString()}</p></div>
                    </div>
                    <button onClick={() => deleteNote(n.id)} data-testid={`crm-delete-note-btn-${n.id}`}><Trash2 className="w-3 h-3 text-red-400 hover:text-red-300" /></button>
                  </div>
                ))}
                {(contact.notes || []).length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
